/**
 * 몬스터. 스폰 주변을 순찰하고, AI 가 있으면 추격·점프·원거리 공격을 한다.
 * 달팽이처럼 ai 가 없는 개체는 원작 초반처럼 무한 순찰만 한다.
 */

import Phaser from 'phaser';
import { monsterAnim } from '../assets/animations';
import type { AssetTable } from '../assets/loader';
import { COMBAT } from '../data/balance';
import type { MonsterDef } from '../data/monsters';
import { DEPTH } from '../game/depth';

export const MONSTER_EVENT = {
  damaged: 'damaged',
  died: 'died',
  shoot: 'shoot',
} as const;

export interface MonsterSpawnInfo {
  def: MonsterDef;
  x: number;
  y: number;
  patrolMin: number;
  patrolMax: number;
}

export class Monster extends Phaser.Physics.Arcade.Sprite {
  readonly events = new Phaser.Events.EventEmitter();
  readonly def: MonsterDef;

  hp: number;
  isDying = false;
  hpBarUntil = 0;
  nextContactAt = 0;
  spawnIndex = -1;
  spawnKey = '';

  private readonly patrolMin: number;
  private readonly patrolMax: number;
  private dir: 1 | -1 = 1;
  private actionTimer = 0;
  private hitStopTimer = 0;
  private lastAttackId = -1;
  private targetX = 0;
  private targetY = 0;
  private aggroUntil = 0;
  private nextJumpAt = 0;
  private nextRangedAt = 0;
  private windupMs = 0;
  private pendingShot: 1 | -1 | 0 = 0;

  constructor(scene: Phaser.Scene, info: MonsterSpawnInfo, assets: AssetTable) {
    super(scene, info.x, info.y, info.def.texture, 0);
    this.def = info.def;
    this.hp = info.def.maxHp;
    this.patrolMin = info.patrolMin;
    this.patrolMax = info.patrolMax;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    const frame = assets.get(info.def.texture);
    const scale = assets.scaleFor(info.def.texture);
    this.setOrigin(0.5, 1).setDepth(DEPTH.monster).setScale(scale);

    const bw = info.def.body.width / scale;
    const bh = info.def.body.height / scale;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(bw, bh, false);
    body.setOffset((frame.frameWidth - bw) / 2, frame.frameHeight - bh);
    body.setDragX(220);

    this.dir = Math.random() < 0.5 ? -1 : 1;
    this.actionTimer = Math.random() * info.def.moveIntervalMs;
    this.setFlipX(this.dir < 0);
    this.play(monsterAnim(info.def.texture), true);
  }

  get centerY(): number {
    return this.y - this.def.body.height / 2;
  }

  get chasing(): boolean {
    return this.scene.time.now < this.aggroUntil;
  }

  /** 매 프레임 플레이어 위치를 알려 준다 */
  setTarget(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  canBeHitBy(attackId: number): boolean {
    return !this.isDying && this.lastAttackId !== attackId;
  }

  noteAttack(attackId: number): void {
    this.lastAttackId = attackId;
  }

  takeDamage(amount: number, attackId: number, fromX: number, knockback: number): void {
    if (this.isDying) return;
    this.lastAttackId = attackId;
    this.hp -= amount;
    this.hpBarUntil = this.scene.time.now + 2200;

    const ai = this.def.ai;
    if (ai && ai.aggroRange > 0) this.aggroUntil = this.scene.time.now + ai.deaggroMs;

    const body = this.body as Phaser.Physics.Arcade.Body;
    const away = this.x < fromX ? -1 : 1;
    body.setVelocityX(knockback * this.def.knockbackResist * away);
    if (body.blocked.down) body.setVelocityY(-110 * this.def.knockbackResist);

    this.hitStopTimer = COMBAT.hitStopMs;
    this.setTintFill(0xffffff);
    this.anims.pause();

    this.events.emit(MONSTER_EVENT.damaged, amount);

    if (this.hp <= 0) this.die();
  }

  private die(): void {
    this.isDying = true;
    this.hp = 0;
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setVelocityY(-200);
    body.checkCollision.none = true;
    this.clearTint();
    this.setTint(0xff9090);
    this.events.emit(MONSTER_EVENT.died, this);

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      angle: this.dir > 0 ? 90 : -90,
      duration: 420,
      ease: 'Quad.In',
      onComplete: () => this.destroy(),
    });
  }

  override preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    if (this.isDying) return;

    if (this.hitStopTimer > 0) {
      this.hitStopTimer -= delta;
      if (this.hitStopTimer <= 0) {
        this.clearTint();
        this.anims.resume();
      }
      return;
    }

    this.refreshAggro(time);
    this.actionTimer -= delta;

    if (this.windupMs > 0) {
      this.windupMs -= delta;
      const body = this.body as Phaser.Physics.Arcade.Body;
      if (body.blocked.down) body.setVelocityX(0);
      if (this.windupMs <= 0 && this.pendingShot) {
        this.events.emit(MONSTER_EVENT.shoot, { monster: this, dir: this.pendingShot });
        this.pendingShot = 0;
      }
      this.setFlipX(this.dir < 0);
      return;
    }

    const chasing = this.chasing;
    const dx = this.targetX - this.x;
    if (chasing && Math.abs(dx) > 10) this.dir = dx > 0 ? 1 : -1;

    if (!chasing) {
      if (this.x <= this.patrolMin) this.dir = 1;
      else if (this.x >= this.patrolMax) this.dir = -1;
    }

    const body = this.body as Phaser.Physics.Arcade.Body;

    if (chasing && this.tryJump(body, time, dx)) {
      this.setFlipX(this.dir < 0);
      return;
    }
    if (chasing && this.tryRanged(time, dx)) {
      this.setFlipX(this.dir < 0);
      return;
    }

    this.move(body, chasing);
    this.setFlipX(this.dir < 0);
  }

  private refreshAggro(now: number): void {
    const ai = this.def.ai;
    if (!ai || ai.aggroRange <= 0) return;
    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    if (Math.hypot(dx, dy) <= ai.aggroRange && Math.abs(dy) <= ai.sameLevelPx) {
      this.aggroUntil = now + ai.deaggroMs;
    }
  }

  private tryJump(body: Phaser.Physics.Arcade.Body, now: number, dx: number): boolean {
    const jump = this.def.ai?.jump;
    if (!jump || !body.blocked.down || now < this.nextJumpAt) return false;
    const dist = Math.abs(dx);
    if (dist < 28 || dist > jump.range) return false;
    body.setVelocity(this.dir * jump.vx, jump.vy);
    this.nextJumpAt = now + jump.cooldownMs;
    return true;
  }

  private tryRanged(now: number, dx: number): boolean {
    const ranged = this.def.ai?.ranged;
    if (!ranged || now < this.nextRangedAt) return false;
    const dist = Math.abs(dx);
    const dy = this.targetY - this.y;
    if (dist < ranged.min || dist > ranged.max || Math.abs(dy) > 72) return false;
    this.nextRangedAt = now + ranged.cooldownMs;
    this.windupMs = 180;
    this.pendingShot = this.dir;
    return true;
  }

  private move(body: Phaser.Physics.Arcade.Body, chasing: boolean): void {
    const speed = chasing ? (this.def.ai?.chaseSpeed ?? this.def.moveSpeed) : this.def.moveSpeed;
    const left = chasing ? this.patrolMin - 12 : this.patrolMin;
    const right = chasing ? this.patrolMax + 12 : this.patrolMax;
    if (this.x <= left) this.dir = 1;
    else if (this.x >= right) this.dir = -1;

    switch (this.def.moveStyle) {
      case 'hop':
        if (this.actionTimer <= 0) {
          this.actionTimer = this.def.moveIntervalMs;
          if (!chasing && Math.random() < 0.2) this.dir = this.dir === 1 ? -1 : 1;
          if (body.blocked.down) {
            body.setVelocityY(this.def.hopVelocity ?? -320);
            body.setVelocityX(speed * this.dir);
          }
        }
        break;
      case 'drift': {
        body.setAllowGravity(false);
        body.setVelocityX(speed * this.dir);
        body.setVelocityY(Math.sin(this.scene.time.now / 420) * 40);
        if (this.actionTimer <= 0) {
          this.actionTimer = this.def.moveIntervalMs;
          if (!chasing && Math.random() < 0.3) this.dir = this.dir === 1 ? -1 : 1;
        }
        break;
      }
      default:
        if (!chasing && this.actionTimer <= 0) {
          this.actionTimer = this.def.moveIntervalMs;
          if (Math.random() < 0.25) this.dir = this.dir === 1 ? -1 : 1;
        }
        if (chasing && Math.abs(this.targetX - this.x) < 14 && body.blocked.down) {
          body.setVelocityX(0);
          break;
        }
        if (body.blocked.down) body.setVelocityX(speed * this.dir);
        break;
    }
  }
}
